import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { EventParticipant } from '@cocoonspace/sdk-js/types/eventParticipant'
import { useEventGuestForm } from '@cocoonspace/shared/domains/events/hooks/use-event-guest-form'
import { getDisplayName } from '@cocoonspace/shared/utils/get-display-name'
import { X } from 'lucide-react'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import type React from 'react'
import type { MouseEventHandler } from 'react'
import { useRef } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import { InputTopGuests } from '~/domains/events/components/molecules/event-input-top-guests'
import { cn } from '~/lib/utils'
import { useUserContext } from '~/providers/user-provider'

export const GuestPill = ({
	user,
	onDeleteClick,
	className,
}: {
	user: EventParticipant
	onDeleteClick?: MouseEventHandler<HTMLButtonElement>
	className?: string
}) => {
	const { t } = useTranslation()

	return (
		<div
			data-testid='participant-pill'
			className={cn(
				'flex flex-row items-center gap-2 rounded-full bg-slate-200/30 py-1 pr-2 pl-3 text-sm',
				className,
			)}
		>
			{getDisplayName({ ...user })}

			<button
				type='button'
				data-testid='delete-email-button'
				title={t('actions:delete.btnLabel') as string}
				className='!h-[20px] !w-[20px] flex items-center justify-center rounded-full bg-slate-200'
				onClick={onDeleteClick}
			>
				<X size={12} />
			</button>
		</div>
	)
}

export const EventShareWidgetContent = ({
	events,
	onInvitationsSent,
}: {
	events: Event[]
	onInvitationsSent?: (guestCount: number) => void
}) => {
	const router = useRouter()
	const { t } = useTranslation()
	const { userQuery } = useUserContext()

	const inputRef = useRef<HTMLInputElement>(null)

	const hasDeskEvents = events?.some(
		({ type, qty }) => type === 'desk' && !!qty,
	)

	const maxGuests = hasDeskEvents
		? events
				.map(({ qty }) => (qty || 0) - 1)
				.sort((a, b) => {
					if (a < b) return -1
					if (a > b) return 1
					return 0
				})
				.pop()
		: undefined

	const {
		addGuests,
		addGuestsFromEmailString,
		removeGuest,
		inputValue,
		setInputValue,
		sendInvites,
		isSendingInvites,
		guests,
		hideCode,
		setHideCode,
	} = useEventGuestForm({
		events,
		hideCode: userQuery.data?.default_hide_codes,
	})

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
			e.preventDefault()

			addGuestsFromEmailString((e.target as HTMLInputElement).value)
		}
	}

	const onSubmit = () =>
		sendInvites()
			.then(() => {
				toast.success(
					t('eventDetails:invitation.success', { count: guests?.length ?? 0 }),
				)

				onInvitationsSent?.(guests?.length ?? 0)
			})
			.catch((err: any) => {
				toast.error(
					t([
						`errors:api.${err.response?.data?.errors[0].code}`,
						'errors:generic',
					]),
				)
			})

	return (
		<div className='flex flex-col'>
			<div className='flex flex-col justify-between gap-y-1 rounded-lg border bg-white p-3 text-xs'>
				<div
					data-testid='guests-list-container'
					className='flex h-16 flex-wrap items-start gap-2 overflow-y-auto'
					onClick={() => inputRef.current?.focus()}
				>
					{guests?.map((guest, i) => (
						<GuestPill
							key={guest.email}
							className='text-xs'
							user={guest}
							onDeleteClick={(e) => {
								e.stopPropagation()
								removeGuest(guest.email)
							}}
						/>
					))}
				</div>

				<InputTopGuests
					ref={inputRef}
					data-testid='emails-input'
					guests={guests || []}
					autoFocus={router.query.autofocus === 'invitation'}
					value={inputValue}
					onKeyDown={handleKeyDown}
					onChange={(e) => setInputValue(e.target.value)}
					onAddEmail={(guest) => addGuests([guest])}
					onBlur={() => addGuestsFromEmailString(inputValue)}
				/>
			</div>

			<div className='mt-4 flex flex-col items-center justify-between gap-y-4 sm:flex-row'>
				{!hasDeskEvents && (
					<label className='cursor-pointer'>
						<Checkbox
							className='mr-1 cursor-pointer accent-primary'
							checked={!hideCode}
							data-testid='hide-code-checkbox'
							onCheckedChange={(checked) => setHideCode(!checked)}
						/>

						<Trans i18nKey='eventDetails:share.shareDoorCode' />
					</label>
				)}

				<Button
					disabled={
						isSendingInvites ||
						!guests?.length ||
						// (!!maxGuests && (event.guests_count || 0) >= maxGuests) ||
						(!!maxGuests && (guests.length || 0) > maxGuests)
					}
					data-testid='send-invites-button'
					className='ml-auto shrink-0'
					onClick={onSubmit}
				>
					{t('eventDetails:edition.invitations.sendBtn', {
						count: guests?.length ?? 0,
					})}
				</Button>
			</div>
		</div>
	)
}
